import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import clsx from "clsx";
import { observer, useLocalStore } from "mobx-react";
import { FunctionComponent, useEffect } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { MediaClip } from "../../../models/SoundClip";
import { ChatViewModel } from "./ViewModel/ChatViewModel";
import ChatMessageList from "./Views/ChatMessageList";
import { EddyEffectP2Results } from "models/EddyEffectP2Results";
import AcxLoadingIndicator from "../AcxLoadingIndicator";
import AcxButton from "../AcxButton";
import AcxToggleButton from "../AcxToggleButton";
import {
    getRedactionToggleItems,
    getTranslationToggleItems,
} from "constants/TranscriptLanguageOptions";

export enum ChatPersona {
    Agent = "agent",
    Customer = "customer",
}

export interface IChatRecord {
    text: string | null;
    timestamp: string;
    from: ChatPersona;
}

interface AugmentedText {
    word: string | null;
    eddy?: boolean;
    viewed?: boolean;
}

export interface IAugmentedChatRecord extends IChatRecord {
    words: AugmentedText[];
    clipId?: string;
    color?: string;
    startIndex?: number;
    eddy?: boolean;
}

interface OwnProps {
    records?: IChatRecord[];
    onClip?: (start: number, end: number) => void;
    mediaClips?: MediaClip[];
    scrollTo?: MediaClip;
    setFocusClip?: (clipId?: string) => void;
    showHeader?: boolean;
    showDivider?: boolean;
    eddyEffectP2Data: EddyEffectP2Results[];
    loading?: boolean;
    secondaryButton?: boolean;
    secondaryButtonLabel?: string;
    secondaryButtonOnClick?: () => void;
    showChatUnredactedUI?: boolean;
    showChatTranslationUI?: boolean;
    translationLanguageCode?: string;
    selectedLanguageCode?: string;
    onChangeLanguage?: (languageCode: string) => void;
    languageToggleId?: string;
    disableClip?: boolean;
    alignControlsRight?: boolean;
}

type Props = OwnProps;

const AcxChatViewer: FunctionComponent<Props> = observer((props) => {
    const viewModel = useLocalStore(() => new ChatViewModel());
    const showLanguageToggle =
        props.showChatUnredactedUI || props.showChatTranslationUI;
    let toggleItems;

    if (showLanguageToggle) {
        toggleItems = props.showChatUnredactedUI
            ? getRedactionToggleItems()
            : getTranslationToggleItems(props.translationLanguageCode);
    }

    useEffect(() => {
        viewModel.setOnClipCallback(props.onClip);
        viewModel.setOnFocusClip(props.setFocusClip);
        viewModel.setActiveMediaClips(props.mediaClips);
        viewModel.setRecords(props.records);
        viewModel.setEddyEffectP2Results(props.eddyEffectP2Data);
    }, [
        props.eddyEffectP2Data,
        props.mediaClips,
        props.onClip,
        props.records,
        props.setFocusClip,
        viewModel,
    ]);

    useEffect(() => {
        if (props.scrollTo) {
            viewModel.clippedMsgRefs.get(props.scrollTo.id)?.scrollIntoView();
        }
    }, [props.scrollTo, viewModel.clippedMsgRefs]);

    return (
        <div className={clsx("pendo-ignore")} style={{ height: "100%" }}>
            <Box>
                <Grid
                    container
                    justifyContent={
                        props.alignControlsRight ? "flex-end" : "space-between"
                    }
                    alignItems="center"
                    sx={{
                        paddingTop: 1.5,
                        paddingRight: 2,
                        paddingBottom: 1.5,
                        paddingLeft: 2,
                    }}
                >
                    {props.showHeader && (
                        <Grid item>
                            <Typography variant="h2">
                                Chat Transcript
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <Grid
                            container
                            alignItems="center"
                            spacing={1}
                            wrap="nowrap"
                        >
                            {viewModel.isClipping && (
                                <Grid item>
                                    <Button
                                        sx={{
                                            textTransform: "none",
                                            fontFamily: (theme) =>
                                                theme.typography.fontFamily,
                                            fontWeight: "bold",
                                            fontSize: "13px",
                                        }}
                                        variant={"contained"}
                                        color={"secondary"}
                                        size={"small"}
                                        onClick={viewModel.clipRecordsInSet}
                                        disableElevation
                                    >
                                        Clip Messages
                                    </Button>
                                </Grid>
                            )}
                            {showLanguageToggle && (
                                <Grid item>
                                    <AcxToggleButton
                                        exclusive
                                        id={
                                            props.languageToggleId ??
                                            "chat-transcript-language-toggle"
                                        }
                                        defaultValue={
                                            props.selectedLanguageCode
                                        }
                                        items={toggleItems}
                                        onChange={(value) => {
                                            if (value) {
                                                props.onChangeLanguage?.(value);
                                            }
                                        }}
                                    />
                                </Grid>
                            )}
                            {props.secondaryButton && (
                                <Grid item>
                                    <AcxButton
                                        fullWidth
                                        style={{
                                            marginRight: 0,
                                        }}
                                        color="white"
                                        onClick={props.secondaryButtonOnClick}
                                    >
                                        {props.secondaryButtonLabel}
                                    </AcxButton>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                {props.showDivider && <Divider variant={"fullWidth"} />}
            </Box>
            <div
                style={
                    {
                        height: `calc(100% - ${
                            props.showHeader ? "114" : "55"
                        }px)`,
                        paddingBottom: 8,
                        paddingTop: 6.4,
                    }
                }
            >
                {props.loading ? (
                    <AcxLoadingIndicator size={24} alternate="PuffLoader" />
                ) : (
                    <Scrollbars>
                        <ChatMessageList
                            records={viewModel.augmentedRecords}
                            viewModel={viewModel}
                            disableClip={props.disableClip || !props.onClip}
                        />
                    </Scrollbars>
                )}
            </div>
        </div>
    );
});

export default AcxChatViewer;
